import { useState } from 'react'
import type { BuildProgressSnapshot, BuildSlotProgress, WatchBuildResult, WatchPart } from '../api/client'
import {
  buildCartPurchaseUrl,
  buildPartPurchaseUrl,
  canBuyFullBuild,
  partHasPurchaseLink,
  providerDisplayName,
  variantIdForPart,
} from '../outbound/links'
import { conversationPermalink } from '../lib/studioSession'
import { CaseVariantPicker } from './CaseVariantPicker'
import {
  CORE_PART_COUNT,
  crownMismatch,
  decisionsByType,
  formatCurrency,
  sortParts,
} from './buildHelpers'
import { UnfulfilledWishCard } from './UnfulfilledWishCard'

type TechnicalLoupeProps = {
  buildResult: WatchBuildResult | null
  buildProgress?: BuildProgressSnapshot | null
  isSending?: boolean
  updatedParts?: Set<string>
  conversationId?: string
  onSuggestionClick?: (suggestion: string) => void
  onPartPurchaseClick?: (part: WatchPart, partType: string) => void
  onBuyFullBuildClick?: (parts: WatchPart[], total: number) => void
  onCaseVariantChange?: (variantId: string) => void
  onRefine?: () => void
  isRestoring?: boolean
  onCopyLink?: () => Promise<void>
}

const SLOT_KEYS: Array<'case' | 'movement' | 'dial' | 'hands' | 'strap'> = [
  'case',
  'movement',
  'dial',
  'hands',
  'strap',
]

const SLOT_LABELS: Record<string, string> = {
  case: 'Case',
  movement: 'Movement',
  dial: 'Dial',
  hands: 'Hands',
  strap: 'Strap',
}

function slotStatusLabel(status: BuildSlotProgress['status']): string {
  switch (status) {
    case 'searching':
      return 'Searching…'
    case 'selected':
    case 'locked':
      return 'Selected'
    case 'failed':
      return 'No match'
    case 'pending':
    default:
      return 'Pending'
  }
}

function ProgressSlotRow({
  slotKey,
  slot,
  isActive,
}: {
  slotKey: string
  slot: BuildSlotProgress
  isActive: boolean
}) {
  const filled = slot.status === 'selected' || slot.status === 'locked'
  const failed = slot.status === 'failed'
  const searching = slot.status === 'searching' || isActive

  let stateClass = ''
  if (filled) stateClass = 'loupe-progress-row--filled'
  else if (failed) stateClass = 'loupe-progress-row--failed'
  else if (searching) stateClass = 'loupe-progress-row--searching'

  return (
    <article className={`loupe-progress-row ${stateClass}`}>
      <div className="loupe-progress-head">
        <span className="loupe-progress-type">{SLOT_LABELS[slotKey] ?? slotKey}</span>
        <span className="loupe-progress-status">
          {searching ? (
            <span className="loupe-progress-dots" aria-hidden="true">
              <span /> <span /> <span />
            </span>
          ) : null}
          {slotStatusLabel(slot.status)}
        </span>
      </div>
      <p className="loupe-progress-name">
        {slot.part_name ?? (searching ? 'Looking for compatible part…' : '—')}
      </p>
    </article>
  )
}

const formatLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

function compactLoupeSpecs(part: WatchPart): string {
  const { compatibility } = part
  const bits: string[] = []
  if (compatibility.case_diameter) bits.push(`${compatibility.case_diameter}mm`)
  if (compatibility.lug_width) bits.push(`${compatibility.lug_width}mm lug`)
  if (compatibility.dial_diameter) bits.push(`${compatibility.dial_diameter}mm dial`)
  if (compatibility.water_resistance) bits.push(`${compatibility.water_resistance}m WR`)
  if (part.material) bits.push(formatLabel(part.material))
  if (compatibility.movement_id) bits.push(compatibility.movement_id)
  return bits.slice(0, 3).join(' · ')
}

function LoupeRow({
  partType,
  part,
  purchaseHref,
  onPurchaseClick,
  onCaseVariantChange,
  isUpdated,
}: {
  partType: string
  part: WatchPart
  purchaseHref: string | null
  onPurchaseClick?: () => void
  onCaseVariantChange?: (variantId: string) => void
  isUpdated?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const providerLabel = providerDisplayName(part.provider_id)
  const specLine = compactLoupeSpecs(part)
  const hasImage = Boolean(part.image_url)

  return (
    <article
      id={`loupe-part-${partType}`}
      className={`loupe-row${isUpdated ? ' loupe-row--updated' : ''}${expanded ? ' loupe-row--expanded' : ''}`}
    >
      <div className="loupe-row-head">
        <span className="loupe-row-type">{formatLabel(partType)}</span>
        <span className="loupe-row-price">{formatCurrency(part.price, part.currency)}</span>
      </div>
      <p className="loupe-row-name">{part.name}</p>
      {specLine ? <p className="loupe-row-specs">{specLine}</p> : null}
      {partType === 'case' ? (
        <CaseVariantPicker part={part} compact onVariantChange={onCaseVariantChange} />
      ) : null}

      {hasImage && expanded ? (
        <figure className="loupe-row-image">
          <img src={part.image_url ?? ''} alt={part.name} loading="lazy" />
        </figure>
      ) : null}

      <div className="loupe-row-foot">
        <span className="loupe-row-provider">{providerLabel}</span>
        <div className="loupe-row-actions">
          {hasImage ? (
            <button
              type="button"
              className="loupe-row-expand"
              aria-expanded={expanded}
              aria-label={expanded ? 'Hide part image' : 'Show part image'}
              onClick={() => setExpanded((v) => !v)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" className={`loupe-chevron${expanded ? ' loupe-chevron--up' : ''}`}>
                <polyline points="4,6 8,10 12,6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : null}
          {purchaseHref ? (
            <a
              className="loupe-row-buy"
              href={purchaseHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onPurchaseClick?.()}
            >
              Buy →
            </a>
          ) : null}
        </div>
      </div>
    </article>
  )
}

export function TechnicalLoupe({
  buildResult,
  buildProgress,
  isSending = false,
  updatedParts,
  conversationId,
  onSuggestionClick,
  onPartPurchaseClick,
  onBuyFullBuildClick,
  onCaseVariantChange,
  onRefine,
  isRestoring = false,
  onCopyLink,
}: TechnicalLoupeProps) {
  const [linkStatus, setLinkStatus] = useState<'idle' | 'saving' | 'copied' | 'failed'>('idle')
  // Live streaming state — show progressive slot reveal as parts come in
  if (!buildResult) {
    const showStreaming = isSending && buildProgress
    const filledCount = showStreaming
      ? SLOT_KEYS.filter((key) => {
          const status = buildProgress!.slots[key]?.status
          return status === 'selected' || status === 'locked'
        }).length
      : 0

    return (
      <aside className="technical-loupe" aria-labelledby="parts-heading">
        <header className="loupe-header">
          <h2 id="parts-heading" className="loupe-kicker">
            Parts
          </h2>
        </header>

        {showStreaming ? (
          <div className="loupe-status">
            <span className="loupe-status-dot loupe-status-dot--searching" />
            <span className="loupe-status-label">
              {filledCount} / {SLOT_KEYS.length} components
            </span>
            {buildProgress!.watch_size_selected ? (
              <span className="loupe-status-size">{buildProgress!.watch_size_selected}</span>
            ) : null}
          </div>
        ) : null}

        {showStreaming ? (
          <div className="loupe-rows">
            {SLOT_KEYS.map((slotKey) => {
              const slot =
                buildProgress!.slots[slotKey] ??
                ({ status: 'pending' as const, part_id: null, part_name: null })
              const isActive = buildProgress!.active_slot === slotKey
              return (
                <ProgressSlotRow
                  key={slotKey}
                  slotKey={slotKey}
                  slot={slot}
                  isActive={isActive}
                />
              )
            })}
          </div>
        ) : (
          <div className="loupe-empty-body">
            <h3 className="loupe-empty-title">
              {isRestoring ? 'Restoring build' : 'Awaiting selection'}
            </h3>
            <p className="loupe-empty-hint">
              {isRestoring
                ? 'Reloading the parts from your previous session.'
                : 'Horatix will list compatible case, movement, dial, hands, and strap here once you describe the watch.'}
            </p>
            <ul className="loupe-slot-placeholders" aria-hidden="true">
              {SLOT_KEYS.map((slotKey) => (
                <li key={slotKey} className="loupe-slot-placeholder">
                  <span className="slot-dot" />
                  {SLOT_LABELS[slotKey]}
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    )
  }

  const parts = sortParts(buildResult.build.parts)
  const decisions = decisionsByType(buildResult.decisions)
  const fallbackCurrency = parts[0]?.[1].currency ?? 'USD'
  const total = parts.reduce((sum, [, part]) => sum + part.price, 0)
  const filledCount = parts.length
  const totalSlots = filledCount + buildResult.failed_steps.length
  const partList = parts.map(([, part]) => part)
  const buildPartIds = partList.map((part) => part.id)
  const showBuyFullBuild = canBuyFullBuild(partList)
  const buyFullBuildProvider = showBuyFullBuild ? providerDisplayName(partList[0].provider_id) : null
  const buyFullBuildHref = showBuyFullBuild
    ? buildCartPurchaseUrl({
        partIds: buildPartIds,
        conversationId,
        variantIds: partList.map((part) => variantIdForPart(part)),
      })
    : null
  const crownMismatchMessage = crownMismatch(parts)

  // touch decisions ref so reuse signal is present (could surface tooltip later)
  void decisions

  return (
    <aside className="technical-loupe" aria-labelledby="parts-heading">
      <header className="loupe-header">
        <h2 id="parts-heading" className="loupe-kicker">
          Parts
        </h2>
      </header>

      <div className="loupe-status">
        <span className="loupe-status-dot" />
        <span className="loupe-status-label">
          {filledCount} / {Math.max(totalSlots, CORE_PART_COUNT)} components
        </span>
        {buildResult.watch_size ? (
          <span className="loupe-status-size">{buildResult.watch_size.selected_size}</span>
        ) : null}
      </div>

      {buildResult.compatibility_errors.length > 0 || crownMismatchMessage ? (
        <div className="loupe-alert" role="alert">
          {buildResult.compatibility_errors.map((compatError) => (
            <p key={compatError}>{compatError}</p>
          ))}
          {crownMismatchMessage ? <p>{crownMismatchMessage}</p> : null}
        </div>
      ) : null}

      <h3 className="sr-only">
        Selected parts
      </h3>

      {isSending && buildProgress ? (
        <section className="loupe-update-progress" aria-label="Live refinement progress">
          <p className="loupe-update-title">Updating this build</p>
          {SLOT_KEYS.map((slotKey) => {
            const slot =
              buildProgress.slots[slotKey] ??
              ({ status: 'pending' as const, part_id: null, part_name: null })
            return (
              <ProgressSlotRow
                key={`update-${slotKey}`}
                slotKey={slotKey}
                slot={slot}
                isActive={buildProgress.active_slot === slotKey}
              />
            )
          })}
          <p className="loupe-update-note">
            Your previous parts remain below until the refinement is complete.
          </p>
        </section>
      ) : null}

      <div className="loupe-rows">
        {parts.map(([partType, part]) => (
          <LoupeRow
            key={part.id}
            partType={partType}
            part={part}
            isUpdated={updatedParts?.has(partType) ?? false}
            purchaseHref={
              partHasPurchaseLink(part)
                ? buildPartPurchaseUrl({
                    partId: part.id,
                    source: 'part_card',
                    conversationId,
                    buildPartIds,
                    variantId: variantIdForPart(part),
                  })
                : null
            }
            onPurchaseClick={() => onPartPurchaseClick?.(part, partType)}
            onCaseVariantChange={partType === 'case' ? onCaseVariantChange : undefined}
          />
        ))}

        {buildResult.failed_steps.map((failure) => (
          <UnfulfilledWishCard
            key={failure.part_type}
            failure={failure}
            onSuggestionClick={onSuggestionClick}
          />
        ))}
        {parts.length === 0 && buildResult.failed_steps.length === 0 ? (
          <div className="loupe-zero-result" role="status">
            <h3>No parts selected</h3>
            <p>
              Horatix could not form a compatible selection from this request. Try widening the
              size, style, or budget.
            </p>
          </div>
        ) : null}
      </div>

      <footer className="loupe-total">
        {parts.length > 0 ? (
          <>
          <div className="loupe-total-meta">
            <p className="loupe-total-label">Total</p>
            <p className="loupe-total-amount">{formatCurrency(total, fallbackCurrency)}</p>
            <p className="loupe-total-sub">
              {parts.length} part{parts.length !== 1 ? 's' : ''}
            </p>
          </div>
          {showBuyFullBuild && buyFullBuildHref ? (
            <a
              className="loupe-buy-full"
              href={buyFullBuildHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onBuyFullBuildClick?.(partList, total)}
            >
              Buy full build at {buyFullBuildProvider}
            </a>
          ) : null}
          </>
        ) : null}
        {onCopyLink ? (
          <>
            <button
              type="button"
              className="loupe-copy-link"
              disabled={linkStatus === 'saving'}
              onClick={() => {
                setLinkStatus('saving')
                void onCopyLink()
                  .then(() => {
                    setLinkStatus('copied')
                    window.setTimeout(() => setLinkStatus('idle'), 2500)
                  })
                  .catch(() => {
                    setLinkStatus('failed')
                  })
              }}
            >
              {linkStatus === 'saving'
                ? 'Copying…'
                : linkStatus === 'copied'
                  ? 'Link copied'
                  : linkStatus === 'failed'
                    ? 'Could not copy'
                    : 'Copy link'}
            </button>
            {linkStatus === 'failed' && conversationId ? (
              <input
                className="loupe-copy-fallback"
                readOnly
                value={conversationPermalink(conversationId)}
                aria-label="Build link"
                onFocus={(event) => event.currentTarget.select()}
              />
            ) : null}
          </>
        ) : null}
        {onRefine ? (
          <button type="button" className="loupe-refine-btn" onClick={onRefine}>
            Refine in conversation
          </button>
        ) : null}
      </footer>
    </aside>
  )
}
